import React from "react";
import { StyleProp, StyleSheet, Text, View, ViewStyle } from "react-native";
import { useSelector } from "react-redux";

import { tBrand, tRep, tVendorData } from "tmt_api/src/types/types.validator";

import { kColors } from "src/kColors";
import { PdButton, PdButtonLabel } from "../forms/button";
import { useVendorSetupAccount } from "src/hooks/modals";
import { kFonts } from "src/kFonts";
import { getDefaultAddress } from "src/util/user";

const BrandSetupAccountDetails: React.FC<{ brand: tBrand; colorScheme?: "white"; style?: StyleProp<ViewStyle> }> = ({
  brand,
  colorScheme,
  style,
}) => {
  const vendorSetupAccount = useVendorSetupAccount();

  const isCsWhite = colorScheme === "white";

  const handleOnPress = () => {
    const address = getDefaultAddress(brand?.addresses ?? []);
    const defaultValue: tVendorData = {
      businessName: brand?.name ?? "",
      businessDomain: brand?.website ?? "",
      ein: brand?.taxId ?? "",
      url: brand?.website ?? "",
      emailAddress: brand?.contacts[0]?.email ?? "",
      address1: address.address1,
      address2: address.address2 ?? "",
      city: address.city,
      state: address.state_or_province,
      zipCode: address.postal_code,
      phone: brand?.contacts[0]?.phone ?? "",
      country: address.country_code,
      accountNumber: "",
      routingNumber: "",
    };
    vendorSetupAccount.openAsync({ mode: "brand", defaultValue });
  };

  if (brand.vendorId) {
    return null;
  }

  return (
    <View style={style}>
      <Text style={isCsWhite ? styles.termsDetailsTitleWhite : styles.termsDetailsTitle}>Activate Your Account</Text>
      <Text style={isCsWhite ? styles.termsDetailsDescriptionWhite : styles.termsDetailsDescription}>
        To activate your account and receive payments, please connect your bank account. Failure to do this will result
        in your account being unpublished.
      </Text>
      <PdButton colorScheme={isCsWhite ? "white" : "green"} variant="outline" onPress={handleOnPress}>
        <PdButtonLabel>Complete application</PdButtonLabel>
      </PdButton>
    </View>
  );
};

const SalesRepSetupAccountDetails: React.FC<{ salesRep: tRep; colorScheme?: "white"; style?: StyleProp<ViewStyle> }> =
  ({ salesRep, colorScheme, style }) => {
    const vendorSetupAccount = useVendorSetupAccount();

    const isCsWhite = colorScheme === "white";

    const handleOnPress = () => {
      const defaultValue: tVendorData = {
        businessName: salesRep?.name ?? "",
        businessDomain: "",
        ein: salesRep?.taxId ?? "",
        url: "",
        emailAddress: salesRep?.email ?? "",
        address1: salesRep.address.address1,
        address2: salesRep.address.address2 ?? "",
        city: salesRep.address.city,
        state: salesRep.address.state_or_province,
        zipCode: salesRep.address.postal_code,
        phone: salesRep.phone ?? "",
        country: salesRep.address.country_code,
        accountNumber: "",
        routingNumber: "",
      };
      vendorSetupAccount.openAsync({ mode: "rep", defaultValue });
    };

    if (salesRep.vendorId) {
      return null;
    }

    return (
      <View style={style}>
        <Text style={isCsWhite ? styles.termsDetailsTitleWhite : styles.termsDetailsTitle}>Activate Your Account</Text>
        <Text style={isCsWhite ? styles.termsDetailsDescriptionWhite : styles.termsDetailsDescription}>
          To activate your account and receive payments, please connect your bank account. Failure to do this will
          result in your account being unpublished.
        </Text>
        <PdButton colorScheme={isCsWhite ? "white" : "green"} variant="outline" onPress={handleOnPress}>
          <PdButtonLabel>Complete application</PdButtonLabel>
        </PdButton>
      </View>
    );
  };

const TermsApplicationDetails: React.FC<{ colorScheme?: "white"; style?: StyleProp<ViewStyle> }> = ({
  colorScheme,
  style,
}) => {
  const rIsCompanyFetching = useSelector((state) => state.CompanyReducer.type === "none");
  const rCompany = useSelector((state) => state.CompanyReducer);

  if (rIsCompanyFetching) {
    return null;
  }

  if (rCompany.type === "brand") {
    return <BrandSetupAccountDetails brand={rCompany} colorScheme={colorScheme} style={style} />;
  }

  if (rCompany.type === "rep") {
    return <SalesRepSetupAccountDetails salesRep={rCompany} colorScheme={colorScheme} style={style} />;
  }

  return null;
};

export default TermsApplicationDetails;

const styles = StyleSheet.create({
  termsDetailsDescription: {
    marginBottom: 10,
    ...kFonts.poppinsExtraSmallLight,
    letterSpacing: 0.5,
    lineHeight: 18,
  },
  termsDetailsDescriptionWhite: {
    marginBottom: 10,
    ...kFonts.poppinsExtraSmallLight,
    color: kColors.white,
    letterSpacing: 0.5,
    lineHeight: 18,
  },
  termsDetailsTitle: {
    textTransform: "uppercase",
    ...kFonts.poppinsSmallBold,
    color: kColors.brandPurple,
    letterSpacing: 1,
    lineHeight: 28,
  },
  termsDetailsTitleWhite: {
    textTransform: "uppercase",
    ...kFonts.poppinsSmallBold,
    color: kColors.white,
    letterSpacing: 1,
    lineHeight: 28,
  },
});
